import { ClientOnly } from "@tanstack/react-router"
import type * as React from "react"

import type { ThemeVariant } from "@repo/ui/components/theming/theme-provider"
import { useTheme } from "@repo/ui/components/theming/theme-provider"
import { Badge } from "@repo/ui/components/ui/badge"
import { Button } from "@repo/ui/components/ui/button"
import { DropdownMenuItem } from "@repo/ui/components/ui/dropdown-menu"
import { MoonIcon, SunIcon, SunMoonIcon } from "@repo/ui/components/ui/icons"
import { cn } from "@repo/ui/lib/utils"
import { logger } from "@repo/utils/logger"

const log = logger.child({ module: "ui/ThemeToggle" })

export type ThemeToggleProps = {
  className?: string
  showLabel?: boolean
  asMenuItem?: boolean
}

const ThemeIcon = ({ theme }: { theme?: ThemeVariant }) => {
  switch (theme) {
    case "light":
      return <SunIcon className="h-[1.2rem] w-[1.2rem]" />
    case "dark":
      return <MoonIcon className="h-[1.2rem] w-[1.2rem]" />
    case "system":
      return <SunMoonIcon className="h-[1.2rem] w-[1.2rem]" />
    default:
      return <SunIcon className="h-[1.2rem] w-[1.2rem]" />
  }
}

/**
 * Theme toggle that switches between light, dark, and system themes.
 * Can be used as a standalone button or as a menu item.
 */
export function ThemeToggle({
  className,
  showLabel = false,
  asMenuItem = false,
}: ThemeToggleProps) {
  const { theme, setTheme } = useTheme()

  const toggleTheme = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    log.debug("Toggling theme", { currentTheme: theme })

    switch (theme) {
      case "light":
        setTheme("dark")
        break
      case "dark":
        setTheme("system")
        break
      default:
        setTheme("light")
    }
  }

  const getThemeLabel = () => {
    switch (theme) {
      case "light":
        return "Light Mode"
      case "dark":
        return "Dark Mode"
      default:
        return "System"
    }
  }

  const getNextThemeLabel = () => {
    switch (theme) {
      case "light":
        return "dark"
      case "dark":
        return "system"
      default:
        return "light"
    }
  }

  const iconAndLabel = (
    <ClientOnly
      fallback={
        <>
          <div className="h-[1.2rem] w-[1.2rem]" />
          {showLabel && <span>Theme</span>}
        </>
      }
    >
      <ThemeIcon theme={theme} />
      {showLabel && (
        <span className="flex w-full items-center justify-between">
          {getThemeLabel()} <Badge variant="outline">Switch</Badge>
        </span>
      )}
    </ClientOnly>
  )

  if (asMenuItem) {
    return (
      <DropdownMenuItem
        onClick={toggleTheme}
        closeOnClick={false}
        className={className}
      >
        {iconAndLabel}
      </DropdownMenuItem>
    )
  }

  if (showLabel) {
    return (
      <Button
        variant="ghost"
        className={cn("gap-2", className)}
        onClick={toggleTheme}
        aria-label={`Switch to ${getNextThemeLabel()} theme`}
      >
        {iconAndLabel}
      </Button>
    )
  }

  return (
    <ClientOnly
      fallback={
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          aria-label="Switch theme"
        />
      }
    >
      <Button
        variant="ghost"
        size="icon"
        className={cn("h-9 w-9", className)}
        onClick={toggleTheme}
        aria-label={`Switch to ${getNextThemeLabel()} theme`}
      >
        <ThemeIcon theme={theme} />
      </Button>
    </ClientOnly>
  )
}
